// TODO(stripe-reimpl): this file has legacy Stripe billing code, disabled while we're betaAccess-only. Rebuild on the new billing system.
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  FormHelperText,
  IconButton,
  InputAdornment,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Add, ContentCopy, Check } from "@mui/icons-material";
import { Controller, useForm } from "react-hook-form";
import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import { loadSeatPricing } from "services/pricingService";
import PricingSummary from "./PricingSummary";

// Roles an admin can invite. Seat/billing impact (providers only, non-beta orgs)
// is surfaced separately by the pricing summary, so it's left out of the copy here.
const ROLE_OPTIONS = [
  {
    value: "admin",
    label: "Admin",
    helper: "Co-manager for prompts, billing, and team.",
  },
  {
    value: "facility",
    label: "Facility",
    helper: "Clinic account. They'll set up or claim their facility on their own during onboarding.",
  },
  {
    value: "professional",
    label: "Provider",
    helper: "Healthcare provider with full ChartMind access.",
  },
  {
    value: "patient",
    label: "Patient",
    helper: "Patient account linked to your organization.",
  },
];

/**
 * InviteTeamDialog - Dialog to invite team members with automatic seat addition
 */
const InviteTeamDialog = ({
  open,
  onClose,
  onInvitationsSent,
  totalSeats,
  usedSeats,
  members,
  invitations,
  subscriptionData,
  isBetaAdmin = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [inviteLinks, setInviteLinks] = useState([]);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [pricePerSeat, setPricePerSeat] = useState(0);
  const [priceInterval, setPriceInterval] = useState("monthly");

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      emails: [""],
      role: "professional",
    },
  });

  const watchedEmails = watch("emails");
  const watchedRole = watch("role");
  // Only professional invites consume seats / trigger pricing.
  const consumesSeats = watchedRole === "professional";

  // Load pricing when dialog opens
  useEffect(() => {
    if (!open || !subscriptionData) return;

    const currentInterval =
      subscriptionData?.items?.[0]?.price?.recurring?.interval === "year"
        ? "yearly"
        : "monthly";

    loadSeatPricing()
      .then((prices) => {
        const priceForInterval = prices[currentInterval];
        if (priceForInterval) {
          setPricePerSeat(priceForInterval.amount);
          setPriceInterval(currentInterval);
        }
      })
      .catch((err) => console.error("Error loading pricing:", err));
  }, [open, subscriptionData]);

  // Reset when dialog closes
  useEffect(() => {
    if (!open) {
      reset({ emails: [""], role: "professional" });
      setError("");
      setSuccess("");
      setInviteLinks([]);
      setCopiedIndex(null);
      setLoading(false);
    }
  }, [open, reset]);

  const handleAddEmailField = () => {
    const currentEmails = watch("emails");
    setValue("emails", [...currentEmails, ""]);
  };

  const handleCopy = (url, index) => {
    navigator.clipboard.writeText(url);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handleInvite = async (data) => {
    setError("");
    setSuccess("");

    // Filter out empty emails
    const validEmails = data.emails.filter((email) => email.trim());

    // Validate we have at least one email
    if (validEmails.length === 0) {
      setError("Please enter at least one email address.");
      return;
    }

    // Check for duplicates in the input
    const uniqueEmails = new Set(validEmails.map((e) => e.toLowerCase()));
    if (uniqueEmails.size !== validEmails.length) {
      setError("You have duplicate email addresses in your list.");
      return;
    }

    // Check if any email is already invited or a member
    for (const email of validEmails) {
      const existingInvite = invitations.find(
        (inv) =>
          inv.email.toLowerCase() === email.toLowerCase() &&
          inv.status === "pending"
      );
      if (existingInvite) {
        setError(`${email} already has a pending invitation.`);
        return;
      }

      const existingMember = members.find(
        (member) => member.email?.toLowerCase() === email.toLowerCase()
      );
      if (existingMember) {
        setError(`${email} is already a member of your organization.`);
        return;
      }
    }

    setLoading(true);

    try {
      const functions = firebase.app().functions("us-central1");

      // Check if we need to add seats. Only professional invites consume seats;
      // beta orgs have unlimited seats — skip in both cases.
      if (!isBetaAdmin && data.role === "professional") {
        const seatsNeeded = usedSeats + validEmails.length;
        if (seatsNeeded > totalSeats) {
          // Add seats first
          const updateSeatQuantity =
            functions.httpsCallable("updateSeatQuantity");
          await updateSeatQuantity({ quantity: seatsNeeded });
        }
      }

      // Send all invitations in parallel
      const sendInvitation = functions.httpsCallable("sendInvitation");
      const results = await Promise.all(
        validEmails.map((email) =>
          sendInvitation({ email: email.toLowerCase(), role: data.role })
        )
      );
      const links = results.map((r) => ({
        email: r.data.email,
        url: r.data.inviteUrl,
      }));
      if (onInvitationsSent) {
        await onInvitationsSent();
      }

      const count = validEmails.length;
      setSuccess(
        `${count} invitation${count === 1 ? "" : "s"} sent successfully.`
      );
      setInviteLinks(links);
      setShowPricing(false); // Hide pricing summary on success
      reset({ emails: [""], role: data.role }); // Clear emails, keep the role
    } catch (err) {
      console.error("Error sending invitation:", err);
      setError(err.message || "Failed to send invitation.");
    } finally {
      setLoading(false);
    }
  };

  // Calculate pricing details - use stable values during loading
  const validEmailCount = watchedEmails.filter((e) => e.trim()).length;
  const seatsNeeded = usedSeats + (consumesSeats ? validEmailCount : 0);
  const additionalSeats = Math.max(0, seatsNeeded - totalSeats);

  // Store the pricing info so it doesn't disappear during loading
  const [showPricing, setShowPricing] = React.useState(false);
  const [pricingInfo, setPricingInfo] = React.useState({
    validEmailCount: 0,
    seatsNeeded: 0,
    additionalSeats: 0,
  });

  React.useEffect(() => {
    if (loading || isBetaAdmin || !consumesSeats) {
      // Beta orgs have unlimited seats, and patient/admin invites never use a
      // seat — never show pricing in those cases.
      setShowPricing(false);
      return;
    }
    if (additionalSeats > 0 && pricePerSeat > 0) {
      setShowPricing(true);
      setPricingInfo({
        validEmailCount,
        seatsNeeded,
        additionalSeats,
      });
    } else {
      setShowPricing(false);
    }
  }, [
    loading,
    isBetaAdmin,
    consumesSeats,
    additionalSeats,
    pricePerSeat,
    validEmailCount,
    seatsNeeded,
  ]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(handleInvite)}>
        <DialogTitle>Invite Team Members</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Invite people to your organization. Choose what kind of account each
            invitation creates.
          </DialogContentText>

          <Controller
            name="role"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                label="Invite as"
                disabled={loading}
                helperText={
                  ROLE_OPTIONS.find((r) => r.value === field.value)?.helper
                }
                sx={{ mb: 3 }}
              >
                {ROLE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          {watchedEmails.map((email, index) => (
            <Controller
              key={index}
              name={`emails.${index}`}
              control={control}
              rules={{
                validate: (value) => {
                  // Allow empty fields
                  if (!value || !value.trim()) {
                    return true;
                  }
                  // Validate non-empty fields
                  const pattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
                  return pattern.test(value) || "Invalid email address.";
                },
              }}
              render={({ field }) => (
                <TextField
                  fullWidth
                  type="email"
                  placeholder="Email Address"
                  disabled={loading}
                  autoFocus={index === 0}
                  error={!!errors?.emails?.[index]}
                  helperText={errors?.emails?.[index]?.message}
                  sx={{ mb: 3 }}
                  {...field}
                />
              )}
            />
          ))}

          <Button
            startIcon={<Add />}
            onClick={handleAddEmailField}
            disabled={loading}
            size="small"
            sx={{ mb: 2 }}
          >
            Add Another
          </Button>

          {/* Pricing summary if adding seats */}
          {showPricing && (
            <Box sx={{ mt: 1, mb: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Inviting{" "}
                {pricingInfo.validEmailCount === 1
                  ? "this user"
                  : "these users"}{" "}
                will add {pricingInfo.additionalSeats} seat
                {pricingInfo.additionalSeats === 1 ? "" : "s"} to your
                organization.
              </Alert>
              <PricingSummary
                currentSeats={totalSeats}
                newSeats={pricingInfo.seatsNeeded}
                pricePerSeat={pricePerSeat}
                interval={priceInterval}
              />
            </Box>
          )}

          {error && (
            <FormHelperText error sx={{ mt: 1 }}>
              {error}
            </FormHelperText>
          )}
          {success && (
            <Alert severity="success" onClose={() => setSuccess("")} sx={{ mt: 1 }}>
              {success}
            </Alert>
          )}
          {inviteLinks.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                We've sent them an email, but if it doesn't arrive, share this
                link directly:
              </Typography>
              {inviteLinks.map(({ email, url }, i) => (
                <Box key={i} sx={{ mb: inviteLinks.length > 1 ? 2 : 0 }}>
                  {inviteLinks.length > 1 && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 0.5 }}
                    >
                      {email}
                    </Typography>
                  )}
                  <TextField
                    value={url}
                    size="small"
                    fullWidth
                    InputProps={{
                      readOnly: true,
                      sx: { fontFamily: "monospace", fontSize: "0.75rem" },
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip
                            title={copiedIndex === i ? "Copied!" : "Copy link"}
                          >
                            <IconButton
                              size="small"
                              onClick={() => handleCopy(url, i)}
                              edge="end"
                            >
                              {copiedIndex === i ? (
                                <Check fontSize="small" color="success" />
                              ) : (
                                <ContentCopy fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Close
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={loading}
            disabled={!watchedEmails.some((e) => e.trim())}
            autoFocus
          >
            Send {validEmailCount > 1 ? "Invitations" : "Invitation"}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default InviteTeamDialog;
